import React, { useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as MuiLab from "@mui/material";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { NewMarketResource } from "api/resource/market";
import { CriterionCard } from "./criterion-card";
import { MarketCard } from "./market-card";
import { styles } from "./style";
import { AppCard } from "component/shared/app-card";

interface TranslatedPreviewProps extends WithStyles<typeof styles> {
	translatedRecords: NewMarketResource[];
	isLoading?: boolean;
	errors?: string[];
	warnings?: string[];
	selectedRecords?: Set<number>;
	onSelectAll?: (checked: boolean) => void;
	onSelectRecord?: (index: number, checked: boolean) => void;
}


const Component: React.FC<TranslatedPreviewProps> = ({
	classes = {},
	translatedRecords = [],
	isLoading = false,
	errors = [],
	warnings = [],
	selectedRecords = new Set(),
	onSelectAll,
	onSelectRecord
}) => {
	const theme = Mui.useTheme();
	const [viewMode, setViewMode] = useState<'cards' | 'json'>('json');
	const [warningsExpanded, setWarningsExpanded] = useState(false);
	const [previewExpanded, setPreviewExpanded] = useState(true);

	const hasRecords = translatedRecords.length > 0;
	const allSelected = hasRecords && selectedRecords.size === translatedRecords.length;
	const someSelected = selectedRecords.size > 0;

	return (
		<AppCard>
			<Mui.CardContent>
				<Mui.Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
					<Mui.Box display="flex" alignItems="center">
						<FeatherIcon className={classes.eyeIcon}>
							<Icons.Eye />
						</FeatherIcon>
						<Mui.Typography variant="h6">
							Translated Records Preview
						</Mui.Typography>
						{hasRecords && (
							<Mui.Chip
								label={`${translatedRecords.length} records`}
								size="small"
								color="primary"
								className={classes.recordsChip}
							/>
						)}
					</Mui.Box>

					<Mui.Box display="flex" alignItems="center" className={classes.headerActions}>
						{hasRecords && previewExpanded && (
							<>
								<Mui.FormControlLabel
									control={
										<Mui.Checkbox
											checked={allSelected}
											indeterminate={someSelected && !allSelected}
											onChange={(e) => onSelectAll?.(e.target.checked)}
											size="small"
										/>
									}
									label={`Select All`}
									className={classes.selectAllLabel}
								/>
								<Mui.ButtonGroup size="small">
									<Mui.Button
										variant={viewMode === 'cards' ? 'contained' : 'outlined'}
										onClick={() => setViewMode('cards')}
										startIcon={<FeatherIcon><Icons.Grid /></FeatherIcon>}
									>
										Cards
									</Mui.Button>
									<Mui.Button
										variant={viewMode === 'json' ? 'contained' : 'outlined'}
										onClick={() => setViewMode('json')}
										startIcon={<FeatherIcon><Icons.Code /></FeatherIcon>}
									>
										JSON
									</Mui.Button>
								</Mui.ButtonGroup>
							</>
						)}
						{hasRecords && (
							<Mui.IconButton
								onClick={() => setPreviewExpanded(!previewExpanded)}
								size="small"
							>
								<FeatherIcon>
									{previewExpanded ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
								</FeatherIcon>
							</Mui.IconButton>
						)}
					</Mui.Box>
				</Mui.Box>

				<Mui.Collapse in={previewExpanded} timeout="auto" unmountOnExit>
					{/* Error and Warning Display */}
					{errors.length > 0 && (
					<MuiLab.Alert severity="error" className={classes.errorAlert}>
						<MuiLab.AlertTitle>Translation Errors</MuiLab.AlertTitle>
						<ul className={classes.errorList}>
							{errors.slice(0, 5).map((error, index) => (
								<li key={index}>{error}</li>
							))}
							{errors.length > 5 && (
								<li>... and {errors.length - 5} more errors</li>
							)}
						</ul>
					</MuiLab.Alert>
				)}

				{warnings.length > 0 && (
					<AppCard className={classes.warningsCard}>
						<Mui.CardHeader
							className={classes.warningsHeader}
							avatar={
								<FeatherIcon className={classes.warningsIcon}>
									<Icons.AlertTriangle color="white"/>
								</FeatherIcon>
							}
							title={
								<Mui.Typography variant="subtitle2" className={classes.warningsTitle}>
									Translation Warnings ({warnings.length})
								</Mui.Typography>
							}
							action={
								<Mui.IconButton
									size="small"
									onClick={() => setWarningsExpanded(!warningsExpanded)}
									className={classes.warningsActionButton}
								>
									<FeatherIcon>
										{warningsExpanded ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
									</FeatherIcon>
								</Mui.IconButton>
							}
						/>
						<Mui.Collapse in={warningsExpanded} timeout="auto" unmountOnExit>
							<Mui.CardContent className={classes.warningsContent}>
								<ul className={classes.warningsList}>
									{warnings.map((warning, index) => (
										<li key={index} className={classes.warningItem}>
											<Mui.Typography variant="body2">{warning}</Mui.Typography>
										</li>
									))}
								</ul>
							</Mui.CardContent>
						</Mui.Collapse>
					</AppCard>
				)}

				{/* Loading State */}
				{isLoading && (
					<Mui.Box display="flex" justifyContent="center" alignItems="center" py={4}>
						<Mui.CircularProgress />
						<Mui.Typography variant="body2" className={classes.loadingText}>
							Translating records...
						</Mui.Typography>
					</Mui.Box>
				)}

				{/* Empty State */}
				{!isLoading && !hasRecords && (
					<Mui.Box textAlign="center" py={4}>
						<FeatherIcon className={classes.emptyStateIcon}>
							<Icons.FileText />
						</FeatherIcon>
						<Mui.Typography variant="h6" color="textSecondary" gutterBottom>
							No Translated Records
						</Mui.Typography>
						<Mui.Typography variant="body2" color="textSecondary">
							Process your Market data to see translated MarketResource records here.
						</Mui.Typography>
					</Mui.Box>
				)}

				{/* Records Display */}
				{!isLoading && hasRecords && (
					<>
						{viewMode === 'cards' ? (
							<Mui.Box>
								{translatedRecords.map((market, index) => (
									<Mui.Box key={index} display="flex" alignItems="flex-start">
										<Mui.Checkbox
											checked={selectedRecords.has(index)}
											onChange={(e) => onSelectRecord?.(index, e.target.checked)}
											className={classes.checkboxCards}
										/>
										<Mui.Box flex={1}>
											<MarketCard market={market} index={index} />
										</Mui.Box>
									</Mui.Box>
								))}
							</Mui.Box>
						) : (
							<Mui.Box>
								{translatedRecords.map((market, index) => (
									<Mui.Box key={index} display="flex" alignItems="flex-start" mb={2}>
										<Mui.Checkbox
											checked={selectedRecords.has(index)}
											onChange={(e) => onSelectRecord?.(index, e.target.checked)}
											className={classes.checkboxJson}
										/>
										<Mui.Box flex={1}>
											<Mui.Paper variant="outlined" className={classes.jsonPaper}>
												<Mui.Typography variant="subtitle2" gutterBottom>
													Record {index + 1}: {market.name}
												</Mui.Typography>
												<pre className={classes.jsonPre}>
													{JSON.stringify(market, null, 2)}
												</pre>
											</Mui.Paper>
										</Mui.Box>
									</Mui.Box>
								))}
							</Mui.Box>
						)}

					</>
				)}
				</Mui.Collapse>
				
				{/* Selection Summary - Always visible */}
				{someSelected && (
					<Mui.Box className={classes.selectionSummary}>
						<Mui.Typography variant="body2" color="primary">
							{selectedRecords.size} of {translatedRecords.length} records selected
						</Mui.Typography>
					</Mui.Box>
				)}
			</Mui.CardContent>
		</AppCard>
	);
};

export const TranslatedPreview = withStyles(Component, styles);